import React, { useState } from "react";
import { Empty, message, Table } from "antd";
import { useTranslation } from "react-i18next";
import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import { FIRESTORE_PACKAGING_STOCKS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../constants/FirestoreConstant";
import { usePackagingStock } from "../../../../data/hooks/firestore/usePackagingStock";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import dayjs from "dayjs";
import StockEditPopover from "components/shared-components/StockEditPopover";

const PackagingStocksTab = ({ tableY, hasEditPermission }) => {
  const { t } = useTranslation();
  const { packagingStocks, isLoading: packagingLoading } = usePackagingStock();
  const { uid } = useAuth();

  const [editingPackaging, setEditingPackaging] = useState(null);
  const [editingPackagingValue, setEditingPackagingValue] = useState(0);
  const [packagingPopoverVisible, setPackagingPopoverVisible] = useState({});

  const handlePackagingStockClick = (record, value) => {
    const popoverKey = record.barcode;

    setPackagingPopoverVisible({});

    setEditingPackaging({
      barcode: record.barcode,
      name: record.name,
      currentStock: value || 0,
    });
    setEditingPackagingValue(0);

    setTimeout(() => {
      setPackagingPopoverVisible({
        [popoverKey]: true,
      });
    }, 0);
  };

  const handleSavePackagingStock = async () => {
    if (!editingPackaging) return;
    try {
      const current = Number(editingPackaging.currentStock || 0);
      const delta = Number(editingPackagingValue || 0);
      const newAbsolute = current + delta;

      const packagingStockRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_PACKAGING_STOCKS_TABLE,
        editingPackaging.barcode,
      );

      await updateDoc(packagingStockRef, {
        stock: newAbsolute,
        updatedAt: dayjs().unix(),
        updatedBy: uid || "system",
      });

      message.success(t("notifications.packaging_stock_updated_successfully"));
    } catch (error) {
      console.error("Error updating packaging stock:", error);
      message.error(t("notifications.failed_to_update_packaging_stock"));
    } finally {
      setPackagingPopoverVisible({});
      setEditingPackaging(null);
    }
  };

  const getPackagingColumns = () => {
    return [
      {
        title: t("barcode"),
        dataIndex: "barcode",
        key: "barcode",
        width: 200,
      },
      {
        title: t("name"),
        dataIndex: "name",
        key: "name",
        width: 200,
      },
      {
        title: t("size"),
        dataIndex: "size",
        key: "size",
        width: 100,
        render: (text) => text?.toUpperCase(),
      },
      {
        title: t("stock"),
        dataIndex: "stock",
        key: "stock",
        width: 150,
        render: (text, record) => {
          const popoverKey = record.barcode;
          const isPopoverVisible = packagingPopoverVisible[popoverKey] === true;
          const currentStock =
            editingPackaging?.barcode === record.barcode
              ? Number(editingPackaging.currentStock || 0)
              : Number(text || 0);

          if (!hasEditPermission) {
            return <span style={{ color: text >= 1 ? "#52c41a" : "#f5222d" }}>{text}</span>;
          }

          return (
            <StockEditPopover
              visible={isPopoverVisible}
              onVisibleChange={(visible) => {
                if (!visible) {
                  setPackagingPopoverVisible((prev) => ({
                    ...prev,
                    [popoverKey]: false,
                  }));
                  if (editingPackaging?.barcode === popoverKey) {
                    setEditingPackaging(null);
                  }
                }
              }}
              currentValue={currentStock}
              editValue={editingPackagingValue}
              onEditValueChange={setEditingPackagingValue}
              onSave={handleSavePackagingStock}
              title={`${t("edit")} ${record.name} ${t("stock")}`}
            >
              <a onClick={() => handlePackagingStockClick(record, text)} style={{ color: "#880e47" }}>
                <span style={{ color: text >= 1 ? "#52c41a" : "#f5222d" }}>{text}</span>
              </a>
            </StockEditPopover>
          );
        },
      },
    ];
  };

  if (packagingLoading) {
    return null;
  }

  return (
    <div className="table-responsive">
      <Table
        showSorterTooltip={false}
        columns={getPackagingColumns()}
        dataSource={packagingStocks}
        rowKey="barcode"
        scroll={{ y: tableY }}
        pagination={false}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_packaging")} />,
        }}
      />
    </div>
  );
};

export default PackagingStocksTab;
